import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { 
  Home, ChevronRight, LayoutDashboard, Package, AlertTriangle, 
  TrendingUp, TrendingDown, Clock, Calendar, DollarSign, 
  BarChart3, Loader2, RefreshCw, ChevronDown, X, FileText,
  Factory, Gauge, Shield, Truck, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// KPI Card Component
const KPICard = ({ label, value, icon: Icon, color = 'cyan', trend, trendDirection, onClick, testId }) => {
  const colorClasses = {
    cyan: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
    amber: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
    red: 'border-red-500/30 bg-red-500/10 text-red-400',
    green: 'border-green-500/30 bg-green-500/10 text-green-400',
    purple: 'border-purple-500/30 bg-purple-500/10 text-purple-400',
    blue: 'border-blue-500/30 bg-blue-500/10 text-blue-400',
    orange: 'border-orange-500/30 bg-orange-500/10 text-orange-400'
  };

  return (
    <div 
      className={`bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-xl p-5 hover:border-${color}-500/50 transition-all duration-300 ${onClick ? 'cursor-pointer hover:shadow-[0_0_20px_rgba(6,182,212,0.1)]' : ''}`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trendDirection === 'up' ? 'text-green-400' : trendDirection === 'down' ? 'text-red-400' : 'text-slate-400'}`}>
            {trendDirection === 'up' ? <TrendingUp className="w-3 h-3" /> : trendDirection === 'down' ? <TrendingDown className="w-3 h-3" /> : null}
            <span>{trend}%</span>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-2xl font-bold text-white font-mono">{value}</p>
    </div>
  );
};

// Section Header Component
const SectionHeader = ({ icon: Icon, title, color = 'cyan' }) => {
  const colorClasses = {
    cyan: 'text-cyan-400 border-cyan-500/30',
    amber: 'text-amber-400 border-amber-500/30',
    red: 'text-red-400 border-red-500/30',
    green: 'text-green-400 border-green-500/30',
    purple: 'text-purple-400 border-purple-500/30',
    blue: 'text-blue-400 border-blue-500/30',
    orange: 'text-orange-400 border-orange-500/30'
  };

  return (
    <div className={`flex items-center gap-3 mb-6 pb-3 border-b ${colorClasses[color]}`}>
      <Icon className={`w-6 h-6 ${colorClasses[color].split(' ')[0]}`} />
      <h2 className="text-xl font-bold text-white uppercase tracking-wide">{title}</h2>
    </div>
  );
};

// Chart colors
const CHART_COLORS = ['#06b6d4', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#3b82f6', '#ec4899', '#14b8a6'];

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('30');
  const [metrics, setMetrics] = useState(null);
  
  // Drill-down dialog states
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownTitle, setDrillDownTitle] = useState('');
  const [drillDownData, setDrillDownData] = useState([]);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [drillDownType, setDrillDownType] = useState('');
  const [monthlyScrapOpen, setMonthlyScrapOpen] = useState(false);
  const [monthlyScrap, setMonthlyScrap] = useState(null);
  const [monthlyScrapLoading, setMonthlyScrapLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; });
  const [scrapPeriod, setScrapPeriod] = useState('monthly');
  const [scrapYear, setScrapYear] = useState(new Date().getFullYear());
  const [scrapQuarter, setScrapQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/dashboard/metrics?days=${dateRange}`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchMetrics();
  };

  // Drill-down handlers
  const fetchScrapReport = async (p, opts = {}) => {
    setMonthlyScrapLoading(true);
    try {
      const params = new URLSearchParams({ period: p });
      if (p === 'monthly') {
        const mo = opts.month || selectedMonth;
        const [y, m] = mo.split('-').map(Number);
        params.set('year', y); params.set('month', m);
      } else if (p === 'quarterly') {
        params.set('year', opts.year || scrapYear); params.set('quarter', opts.quarter || scrapQuarter);
      } else {
        params.set('year', opts.year || scrapYear);
      }
      const res = await axios.get(`${BACKEND_URL}/api/dashboard/scrap-report?${params.toString()}`);
      setMonthlyScrap(res.data);
    } catch { toast.error('Failed to load scrap report'); }
    finally { setMonthlyScrapLoading(false); }
  };

  const openScrapReport = () => {
    setMonthlyScrapOpen(true);
    fetchScrapReport(scrapPeriod);
  };

  const handlePeriodChange = (p) => {
    setScrapPeriod(p);
    fetchScrapReport(p);
  };

  const exportScrapReport = () => {
    if (!monthlyScrap) return;
    const wb = XLSX.utils.book_new();
    const d = monthlyScrap;
    const periodLabel = d.label || '';

    // Summary sheet
    const sumRows = [
      [`Scrap Report — ${periodLabel}`],
      [''], ['SUMMARY'],
      ['Total Scrap Value', d.summary.total_value],
      ['Total Scrap Quantity', d.summary.total_quantity],
      ['Scrap Incidents', d.summary.incident_count],
      ['Scrap % of Production', `${d.summary.scrap_percentage}%`],
    ];
    if (d.period === 'monthly') {
      sumRows.push(['Previous Month Value', d.summary.prev_period_value]);
      sumRows.push(['Change vs Prev', `${d.summary.value_change_pct}%`]);
    } else if (d.period === 'quarterly') {
      sumRows.push(['Previous Quarter Value', d.summary.prev_quarter_value]);
      sumRows.push(['Change vs Prev Quarter', `${d.summary.value_change_pct}%`]);
    } else if (d.period === 'annual') {
      sumRows.push(['Average Monthly Scrap', d.summary.average_monthly]);
      sumRows.push(['Highest Month', `${d.summary.highest_month} (${d.summary.highest_month_value})`]);
      sumRows.push(['Lowest Month', `${d.summary.lowest_month} (${d.summary.lowest_month_value})`]);
      sumRows.push(['Previous Year Value', d.summary.prev_year_value]);
      sumRows.push(['Year-on-Year Change', `${d.summary.yoy_change_pct}%`]);
    }
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(sumRows), 'Summary');

    // Monthly trend sheet (quarterly & annual)
    if (d.monthly_trend) {
      const trendRows = [['Month', 'Value', 'Quantity']];
      d.monthly_trend.forEach(m => trendRows.push([m.month, m.value, m.quantity]));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(trendRows), 'Monthly Trend');
    }

    // Component sheet
    if (d.by_component?.length > 0) {
      const compRows = [['Part Number', 'Description', 'Scrap Qty', 'Scrap Value', '% of Total', 'Orders']];
      d.by_component.forEach(c => compRows.push([c.part_number, c.description, c.scrap_quantity, c.scrap_value, c.percentage_of_total + '%', c.order_count]));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(compRows), 'By Component');
    }

    // Department sheet
    if (d.by_department?.length > 0) {
      const deptRows = [['Department', 'Scrap Qty', 'Scrap Value', '% of Total']];
      d.by_department.forEach(dd => deptRows.push([dd.department, dd.scrap_quantity, dd.scrap_value, dd.percentage_of_total + '%']));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(deptRows), 'By Department');
    }

    // Reason sheet
    if (d.by_reason?.length > 0) {
      const resRows = [['Reason', 'Count', 'Qty Affected']];
      d.by_reason.forEach(r => resRows.push([r.reason, r.count, r.quantity]));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resRows), 'By Reason');
    }

    // Year-on-Year sheet (annual only)
    if (d.period === 'annual' && d.prev_year_trend) {
      const yoyRows = [['Month', `${d.year} Value`, `${d.year - 1} Value`]];
      d.monthly_trend.forEach((m, i) => yoyRows.push([m.month, m.value, d.prev_year_trend[i]?.value || 0]));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(yoyRows), 'Year-on-Year');
    }

    XLSX.writeFile(wb, `Scrap_Report_${periodLabel.replace(/\s/g, '_')}.xlsx`);
    toast.success('Report exported');
  };

  const openDrillDown = async (type, title, params = {}) => {
    setDrillDownTitle(title);
    setDrillDownType(type);
    setDrillDownOpen(true);
    setDrillDownLoading(true);

    try {
      let endpoint = '';
      const queryParams = new URLSearchParams({ days: dateRange, ...params }).toString();
      
      switch (type) {
        case 'orders':
          endpoint = `/api/dashboard/order-book?${queryParams}`;
          break;
        case 'scrap':
          endpoint = `/api/dashboard/scrap-details?${queryParams}`;
          break;
        case 'ncr':
          endpoint = `/api/dashboard/ncr-details?${queryParams}`;
          break;
        case 'delivery':
          endpoint = `/api/dashboard/delivery-details?${queryParams}`;
          break;
        default:
          break;
      }

      if (endpoint) {
        const response = await axios.get(`${BACKEND_URL}${endpoint}`);
        setDrillDownData(response.data);
      }
    } catch (error) {
      console.error('Error fetching drill-down data:', error);
      toast.error('Failed to load details');
    } finally {
      setDrillDownLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-cyan-400 mx-auto mb-4" />
          <p className="text-slate-400 uppercase tracking-wider text-sm">Loading Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden text-white">
      {/* Honeycomb Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-dash" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(6, 182, 212, 0.12)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(6, 182, 212, 0.12)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-dash)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(6, 182, 212, 0.06) 0%, transparent 70%)' }} />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-cyan-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10" data-testid="back-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center">
                <LayoutDashboard className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide" data-testid="page-title">Management Dashboard</h1>
                <p className="text-xs text-slate-500">Operational Control Centre</p>
              </div>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-[140px] bg-slate-950/50 border-slate-800" data-testid="date-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">Last 5 Days</SelectItem>
                  <SelectItem value="30">Last 30 Days</SelectItem>
                  <SelectItem value="90">Last 90 Days</SelectItem>
                  <SelectItem value="180">Last 6 Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="border-slate-700 hover:border-cyan-500/50"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Breadcrumbs */}
          <nav className="hidden lg:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Home</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-cyan-400 font-medium">Dashboard</span>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[1800px] mx-auto space-y-8">

          {/* =============== ORDER BOOK SECTION =============== */}
          <section data-testid="order-book-section">
            <SectionHeader icon={Package} title="Outstanding Order Book" color="cyan" />
            
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KPICard
                label="Open Order Value"
                value={formatCurrency(metrics?.order_book?.total_open_value || 0)}
                icon={DollarSign}
                color="cyan"
                onClick={() => openDrillDown('orders', 'Open Orders', { status: 'open' })}
                testId="kpi-open-value"
              />
              <KPICard
                label="Closed Value (5 Days)"
                value={formatCurrency(metrics?.order_book?.total_closed_value_5days || 0)}
                icon={TrendingUp}
                color="green"
                onClick={() => openDrillDown('orders', 'Closed Orders (5 Days)', { status: 'closed', days: 5 })}
                testId="kpi-closed-value"
              />
              <KPICard
                label="Components Outstanding"
                value={(metrics?.order_book?.total_components_outstanding || 0).toLocaleString()}
                icon={Factory}
                color="amber"
                testId="kpi-components"
              />
              <KPICard
                label="Orders Outstanding"
                value={metrics?.order_book?.total_orders_outstanding || 0}
                icon={FileText}
                color="purple"
                onClick={() => openDrillDown('orders', 'Outstanding Orders', { status: 'open' })}
                testId="kpi-orders"
              />
            </div>

            {/* Department Value Breakdown Chart */}
            <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-xl p-6">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Order Value by Department</h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={metrics?.order_book?.process_value_breakdown || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis type="number" tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} stroke="#94a3b8" />
                    <YAxis type="category" dataKey="process_name" width={150} stroke="#94a3b8" tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px' }}
                    />
                    <Bar dataKey="value" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

          {/* =============== SCRAP REPORT SECTION =============== */}
          <section data-testid="scrap-section">
            <SectionHeader icon={AlertTriangle} title="Scrap Report" color="red" />
            
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KPICard
                label="Scrap Rate (30 Days)"
                value={`${metrics?.scrap?.scrap_rate_percentage || 0}%`}
                icon={Gauge}
                color="red"
                testId="kpi-scrap-rate"
              />
              <KPICard
                label="Scrap Value (30 Days)"
                value={formatCurrency(metrics?.scrap?.scrap_value_30days || 0)}
                icon={TrendingDown}
                color="orange"
                onClick={() => openDrillDown('scrap', 'Scrap Details (30 Days)', { days: 30 })}
                testId="kpi-scrap-30"
              />
              <KPICard
                label="Parts Scrapped (30 Days)"
                value={(metrics?.scrap?.total_parts_scrapped || 0).toLocaleString()}
                icon={AlertTriangle}
                color="amber"
                testId="kpi-parts-scrapped"
              />
              <KPICard
                label="Parts Inspected (30 Days)"
                value={(metrics?.scrap?.invoiced_parts_30days || 0).toLocaleString()}
                icon={Factory}
                color="green"
                testId="kpi-inspected-parts"
              />
            </div>

            {/* Scrap Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Scrap % by Value */}
              <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-xl p-6">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Scrap % by Value (Department)</h3>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics?.scrap?.department_scrap_30days || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="department" stroke="#94a3b8" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#94a3b8" tickFormatter={(v) => `${v}%`} />
                      <Tooltip 
                        formatter={(value, name) => [name === 'scrap_percentage' ? `${value}%` : formatCurrency(value), name === 'scrap_percentage' ? 'Scrap %' : 'Scrap Value']}
                        contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px' }}
                      />
                      <Bar dataKey="scrap_percentage" fill="#ef4444" radius={[4, 4, 0, 0]} name="Scrap %" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Scrap % by Quantity */}
              <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-xl p-6">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Scrap % by Quantity (Department)</h3>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics?.scrap?.department_scrap_30days || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="department" stroke="#94a3b8" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#94a3b8" tickFormatter={(v) => `${v}%`} />
                      <Tooltip 
                        formatter={(value, name) => [`${value}%`, 'Scrap %']}
                        contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px' }}
                      />
                      <Bar dataKey="scrap_quantity_percentage" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Scrap %" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </section>

          {/* =============== SCRAP REPORT PERIOD SELECTOR =============== */}
          <section data-testid="scrap-report-section">
            <SectionHeader icon={Calendar} title="Scrap Report" color="orange" />
            <div className="bg-slate-900/40 backdrop-blur-md border border-orange-500/20 rounded-xl p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <p className="text-sm text-slate-400">ISO 9001 traceable scrap analysis — Monthly, Quarterly &amp; Annual</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Period toggle */}
                  <div className="flex bg-slate-800 rounded-lg overflow-hidden border border-slate-700" data-testid="scrap-period-toggle">
                    {['monthly', 'quarterly', 'annual'].map(p => (
                      <button
                        key={p}
                        onClick={() => setScrapPeriod(p)}
                        className={`px-3 py-1.5 text-xs uppercase font-bold transition-colors ${scrapPeriod === p ? 'bg-orange-500/20 text-orange-400' : 'text-slate-500 hover:text-white'}`}
                        data-testid={`scrap-period-${p}`}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  {/* Period-specific selector */}
                  {scrapPeriod === 'monthly' && (
                    <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white" data-testid="month-select" />
                  )}
                  {scrapPeriod === 'quarterly' && (
                    <div className="flex items-center gap-1">
                      <select value={scrapQuarter} onChange={(e) => setScrapQuarter(Number(e.target.value))} className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white" data-testid="quarter-select">
                        <option value={1}>Q1</option><option value={2}>Q2</option><option value={3}>Q3</option><option value={4}>Q4</option>
                      </select>
                      <select value={scrapYear} onChange={(e) => setScrapYear(Number(e.target.value))} className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white" data-testid="scrap-year-select">
                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  )}
                  {scrapPeriod === 'annual' && (
                    <select value={scrapYear} onChange={(e) => setScrapYear(Number(e.target.value))} className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white" data-testid="scrap-year-select-annual">
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  )}
                  <Button size="sm" onClick={openScrapReport} className="bg-orange-500/20 text-orange-400 border border-orange-500/50 font-bold uppercase text-xs" data-testid="open-scrap-report-btn">
                    View Full Report
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Current Period Value</p>
                  <p className="text-xl font-bold font-mono text-red-400">{formatCurrency(metrics?.scrap?.scrap_value_30days || 0)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Current Period Qty</p>
                  <p className="text-xl font-bold font-mono text-orange-400">{(metrics?.scrap?.total_parts_scrapped || 0).toLocaleString()}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Scrap Rate</p>
                  <p className="text-xl font-bold font-mono text-amber-400">{metrics?.scrap?.scrap_rate_percentage || 0}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">30-Day Trend</p>
                  <p className="text-xl font-bold font-mono text-slate-400">{formatCurrency(metrics?.scrap?.scrap_value_5days || 0)} <span className="text-xs text-slate-600">(5d)</span></p>
                </div>
              </div>
            </div>
          </section>

          {/* =============== QUALITY REPORT SECTION =============== */}
          <section data-testid="quality-section">
            <SectionHeader icon={Shield} title="Quality Report" color="purple" />
            
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <KPICard
                label="External NCRs (3 Months)"
                value={metrics?.quality?.external_ncrs_3months || 0}
                icon={AlertTriangle}
                color="red"
                onClick={() => openDrillDown('ncr', 'External NCRs', { ncr_type: 'External', days: 90 })}
                testId="kpi-external-ncr"
              />
              <KPICard
                label="Internal NCRs (3 Months)"
                value={metrics?.quality?.internal_ncrs_3months || 0}
                icon={Shield}
                color="purple"
                onClick={() => openDrillDown('ncr', 'Internal NCRs', { ncr_type: 'Internal', days: 90 })}
                testId="kpi-internal-ncr"
              />
              <KPICard
                label="Avg Close-Out (Days)"
                value={`${metrics?.quality?.average_closeout_days || 0} days`}
                icon={Clock}
                color="blue"
                testId="kpi-closeout"
              />
            </div>

            {/* NCR Breakdown Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-xl p-6">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">NCRs by Department</h3>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={metrics?.quality?.department_ncr_breakdown || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="department" stroke="#94a3b8" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#94a3b8" />
                      <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px' }} />
                      <Legend />
                      <Bar dataKey="internal_count" name="Internal" fill="#8b5cf6" stackId="a" />
                      <Bar dataKey="external_count" name="External" fill="#ef4444" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* NCR Pie Chart */}
              <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-xl p-6">
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">NCR Distribution</h3>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={metrics?.quality?.department_ncr_breakdown?.filter(d => d.total_count > 0) || []}
                        dataKey="total_count"
                        nameKey="department"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={{ stroke: '#64748b' }}
                      >
                        {(metrics?.quality?.department_ncr_breakdown || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </section>

          {/* =============== DELIVERY PERFORMANCE SECTION =============== */}
          <section data-testid="delivery-section">
            <SectionHeader icon={Truck} title="Delivery Performance" color="green" />
            
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <KPICard
                label="Avg Delivery Rate"
                value={`${metrics?.delivery?.average_delivery_days || 0} days`}
                icon={Clock}
                color="green"
                onClick={() => openDrillDown('delivery', 'Delivery Performance Details')}
                testId="kpi-avg-delivery"
              />
              <KPICard
                label="On-Time Delivery %"
                value={`${metrics?.delivery?.on_time_delivery_percentage || 0}%`}
                icon={TrendingUp}
                color={metrics?.delivery?.on_time_delivery_percentage >= 90 ? 'green' : metrics?.delivery?.on_time_delivery_percentage >= 70 ? 'amber' : 'red'}
                onClick={() => openDrillDown('delivery', 'On-Time Delivery Breakdown')}
                testId="kpi-ontime-pct"
              />
              <KPICard
                label="Total Orders Measured"
                value={metrics?.delivery?.total_orders_measured || 0}
                icon={Truck}
                color="blue"
                testId="kpi-orders-measured"
              />
            </div>

            {/* On-Time vs Late Summary */}
            {(metrics?.delivery?.total_orders_measured || 0) > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                <div className="bg-slate-900/40 backdrop-blur-md border border-green-500/20 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">On-Time Orders</p>
                      <p className="text-3xl font-bold text-green-400 font-mono">{metrics?.delivery?.on_time_count || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-400" />
                    </div>
                  </div>
                </div>
                <div className="bg-slate-900/40 backdrop-blur-md border border-red-500/20 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Late Orders</p>
                      <p className="text-3xl font-bold text-red-400 font-mono">{metrics?.delivery?.late_count || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                      <TrendingDown className="w-6 h-6 text-red-400" />
                    </div>
                  </div>
                </div>
                <div className="bg-slate-900/40 backdrop-blur-md border border-cyan-500/20 rounded-xl p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">On-Time Rate</p>
                      <div className="mt-2">
                        <div className="w-full bg-slate-800 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full transition-all duration-500 ${
                              (metrics?.delivery?.on_time_delivery_percentage || 0) >= 90 ? 'bg-green-500' : 
                              (metrics?.delivery?.on_time_delivery_percentage || 0) >= 70 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(metrics?.delivery?.on_time_delivery_percentage || 0, 100)}%` }}
                          />
                        </div>
                        <p className="text-sm text-slate-400 mt-1 font-mono">{metrics?.delivery?.on_time_delivery_percentage || 0}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Department Lead Times */}
            <div className="bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-xl p-6">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Average Days per Department</h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={metrics?.delivery?.department_averages || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis type="number" stroke="#94a3b8" />
                    <YAxis type="category" dataKey="department" width={150} stroke="#94a3b8" tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value) => [`${value.toFixed(1)} days`, 'Avg Time']}
                      contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px' }}
                    />
                    <Bar dataKey="average_days" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* Drill-Down Dialog */}
      <Dialog open={drillDownOpen} onOpenChange={setDrillDownOpen}>
        <DialogContent className="bg-slate-950 border-cyan-500/30 max-w-4xl max-h-[80vh] overflow-hidden" data-testid="drill-down-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-cyan-400" />
              {drillDownTitle}
            </DialogTitle>
            <p className="text-sm text-slate-400">Click on any row to view more details</p>
          </DialogHeader>

          {drillDownLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            </div>
          ) : (
            <div className="overflow-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-900">
                  <tr className="border-b border-slate-800">
                    {drillDownType === 'orders' && (
                      <>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Order #</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Customer</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Part</th>
                        <th className="text-right p-3 text-slate-400 uppercase text-xs">Qty</th>
                        <th className="text-right p-3 text-slate-400 uppercase text-xs">Value</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Stage</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Due Date</th>
                      </>
                    )}
                    {drillDownType === 'scrap' && (
                      <>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Order #</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Part</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Process</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Department</th>
                        <th className="text-right p-3 text-slate-400 uppercase text-xs">Scrap Qty</th>
                        <th className="text-right p-3 text-slate-400 uppercase text-xs">Scrap Value</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Date</th>
                      </>
                    )}
                    {drillDownType === 'ncr' && (
                      <>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">NCR #</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Type</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Department</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Severity</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Status</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Date Raised</th>
                        <th className="text-right p-3 text-slate-400 uppercase text-xs">Close Days</th>
                      </>
                    )}
                    {drillDownType === 'delivery' && (
                      <>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Order #</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Customer</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Part</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Created</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Delivered</th>
                        <th className="text-left p-3 text-slate-400 uppercase text-xs">Due Date</th>
                        <th className="text-right p-3 text-slate-400 uppercase text-xs">Rate (Days)</th>
                        <th className="text-center p-3 text-slate-400 uppercase text-xs">Status</th>
                        <th className="text-right p-3 text-slate-400 uppercase text-xs">Days Late</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {drillDownData.map((item) => (
                    <tr key={item.order_number || item.part_number || Math.random()} className="border-b border-slate-800/50 hover:bg-slate-900/50">
                      {drillDownType === 'orders' && (
                        <>
                          <td className="p-3 font-mono text-cyan-400">{item.order_number}</td>
                          <td className="p-3 text-white">{item.customer}</td>
                          <td className="p-3 font-mono text-slate-300">{item.part_number}</td>
                          <td className="p-3 text-right text-white">{item.quantity}</td>
                          <td className="p-3 text-right text-amber-400 font-mono">{formatCurrency(item.order_value)}</td>
                          <td className="p-3"><span className="px-2 py-1 rounded text-xs bg-slate-800 text-slate-300">{item.current_stage}</span></td>
                          <td className="p-3 text-slate-400">{item.due_date?.split('T')[0]}</td>
                        </>
                      )}
                      {drillDownType === 'scrap' && (
                        <>
                          <td className="p-3 font-mono text-cyan-400">{item.order_number}</td>
                          <td className="p-3 font-mono text-slate-300">{item.part_number}</td>
                          <td className="p-3 text-white">{item.process}</td>
                          <td className="p-3 text-slate-400">{item.department}</td>
                          <td className="p-3 text-right text-red-400 font-bold">{item.scrap_quantity}</td>
                          <td className="p-3 text-right text-red-400 font-mono">{formatCurrency(item.scrap_value)}</td>
                          <td className="p-3 text-slate-400">{item.date}</td>
                        </>
                      )}
                      {drillDownType === 'ncr' && (
                        <>
                          <td className="p-3 font-mono text-cyan-400">{item.ncr_number}</td>
                          <td className="p-3"><span className={`px-2 py-1 rounded text-xs ${item.ncr_type === 'External' ? 'bg-red-500/20 text-red-400' : 'bg-purple-500/20 text-purple-400'}`}>{item.ncr_type}</span></td>
                          <td className="p-3 text-white">{item.department}</td>
                          <td className="p-3"><span className={`px-2 py-1 rounded text-xs ${item.severity === 'Critical' ? 'bg-red-500/20 text-red-400' : item.severity === 'Medium' ? 'bg-orange-500/20 text-orange-400' : 'bg-green-500/20 text-green-400'}`}>{item.severity}</span></td>
                          <td className="p-3 text-slate-300">{item.status}</td>
                          <td className="p-3 text-slate-400">{item.date_raised?.split('T')[0]}</td>
                          <td className="p-3 text-right text-white">{item.closeout_days !== null ? `${item.closeout_days}d` : '-'}</td>
                        </>
                      )}
                      {drillDownType === 'delivery' && (
                        <>
                          <td className="p-3 font-mono text-cyan-400">{item.order_number}</td>
                          <td className="p-3 text-white">{item.customer}</td>
                          <td className="p-3 font-mono text-slate-300">{item.part_number}</td>
                          <td className="p-3 text-slate-400">{item.created_at?.split('T')[0]}</td>
                          <td className="p-3 text-slate-400">{item.completed_at?.split('T')[0]}</td>
                          <td className="p-3 text-slate-400">{item.due_date?.split('T')[0]}</td>
                          <td className="p-3 text-right text-green-400 font-bold font-mono">{item.delivery_rate_days}d</td>
                          <td className="p-3 text-center">
                            {item.on_time === true && <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">On-Time</span>}
                            {item.on_time === false && <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-400">Late</span>}
                            {item.on_time === null && <span className="px-2 py-1 rounded text-xs bg-slate-500/20 text-slate-400">N/A</span>}
                          </td>
                          <td className="p-3 text-right font-mono">{item.days_late > 0 ? <span className="text-red-400">{item.days_late}d</span> : <span className="text-slate-500">-</span>}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              {drillDownData.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No data found for the selected criteria
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* =============== SCRAP REPORT DIALOG =============== */}
      <Dialog open={monthlyScrapOpen} onOpenChange={setMonthlyScrapOpen}>
        <DialogContent className="bg-slate-950 border-orange-500/20 max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Scrap Report {monthlyScrap ? `— ${monthlyScrap.label}` : ''}
            </DialogTitle>
          </DialogHeader>

          {/* Period toggle + selector + export */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex bg-slate-800 rounded-lg overflow-hidden border border-slate-700" data-testid="dialog-period-toggle">
                {['monthly', 'quarterly', 'annual'].map(p => (
                  <button
                    key={p}
                    onClick={() => handlePeriodChange(p)}
                    className={`px-3 py-1.5 text-xs uppercase font-bold transition-colors ${scrapPeriod === p ? 'bg-orange-500/20 text-orange-400' : 'text-slate-500 hover:text-white'}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              {scrapPeriod === 'monthly' && (
                <input type="month" value={selectedMonth} onChange={(e) => { setSelectedMonth(e.target.value); fetchScrapReport('monthly', { month: e.target.value }); }} className="bg-slate-900 border border-slate-700 rounded px-3 py-1.5 text-sm text-white" />
              )}
              {scrapPeriod === 'quarterly' && (
                <div className="flex items-center gap-1">
                  <select value={scrapQuarter} onChange={(e) => { const q = Number(e.target.value); setScrapQuarter(q); fetchScrapReport('quarterly', { quarter: q, year: scrapYear }); }} className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
                    <option value={1}>Q1</option><option value={2}>Q2</option><option value={3}>Q3</option><option value={4}>Q4</option>
                  </select>
                  <select value={scrapYear} onChange={(e) => { const y = Number(e.target.value); setScrapYear(y); fetchScrapReport('quarterly', { quarter: scrapQuarter, year: y }); }} className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              )}
              {scrapPeriod === 'annual' && (
                <select value={scrapYear} onChange={(e) => { const y = Number(e.target.value); setScrapYear(y); fetchScrapReport('annual', { year: y }); }} className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              )}
            </div>
            <Button size="sm" onClick={exportScrapReport} disabled={!monthlyScrap} className="bg-green-500/20 text-green-400 border border-green-500/50" data-testid="export-scrap-excel-btn">
              <Download className="w-4 h-4 mr-1" /> Export Excel
            </Button>
          </div>

          {monthlyScrapLoading && <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>}

          {monthlyScrap && !monthlyScrapLoading && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-red-950/20 border border-red-500/20 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-red-400 uppercase">Total Scrap Value</p>
                  <p className="text-xl font-bold font-mono text-red-400">{formatCurrency(monthlyScrap.summary.total_value)}</p>
                </div>
                <div className="bg-orange-950/20 border border-orange-500/20 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-orange-400 uppercase">Total Scrap Qty</p>
                  <p className="text-xl font-bold font-mono text-orange-400">{monthlyScrap.summary.total_quantity}</p>
                </div>
                <div className="bg-blue-950/20 border border-blue-500/20 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-blue-400 uppercase">Scrap % of Production</p>
                  <p className="text-xl font-bold font-mono text-blue-400">{monthlyScrap.summary.scrap_percentage}%</p>
                </div>
                {/* Period comparison */}
                {monthlyScrap.period === 'monthly' && (
                  <div className={`border rounded-lg p-3 text-center ${(monthlyScrap.summary.value_change_pct || 0) > 0 ? 'bg-red-950/20 border-red-500/20' : 'bg-green-950/20 border-green-500/20'}`}>
                    <p className="text-[10px] uppercase text-slate-400">vs Prev Month</p>
                    <p className={`text-xl font-bold font-mono ${(monthlyScrap.summary.value_change_pct || 0) > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {(monthlyScrap.summary.value_change_pct || 0) > 0 ? '+' : ''}{monthlyScrap.summary.value_change_pct || 0}%
                    </p>
                  </div>
                )}
                {monthlyScrap.period === 'quarterly' && (
                  <div className={`border rounded-lg p-3 text-center ${(monthlyScrap.summary.value_change_pct || 0) > 0 ? 'bg-red-950/20 border-red-500/20' : 'bg-green-950/20 border-green-500/20'}`}>
                    <p className="text-[10px] uppercase text-slate-400">vs Prev Quarter</p>
                    <p className={`text-xl font-bold font-mono ${(monthlyScrap.summary.value_change_pct || 0) > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {(monthlyScrap.summary.value_change_pct || 0) > 0 ? '+' : ''}{monthlyScrap.summary.value_change_pct || 0}%
                    </p>
                  </div>
                )}
                {monthlyScrap.period === 'annual' && (
                  <div className={`border rounded-lg p-3 text-center ${(monthlyScrap.summary.yoy_change_pct || 0) > 0 ? 'bg-red-950/20 border-red-500/20' : 'bg-green-950/20 border-green-500/20'}`}>
                    <p className="text-[10px] uppercase text-slate-400">vs Prev Year</p>
                    <p className={`text-xl font-bold font-mono ${(monthlyScrap.summary.yoy_change_pct || 0) > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {(monthlyScrap.summary.yoy_change_pct || 0) > 0 ? '+' : ''}{monthlyScrap.summary.yoy_change_pct || 0}%
                    </p>
                  </div>
                )}
              </div>

              {/* Annual-specific KPIs */}
              {monthlyScrap.period === 'annual' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-slate-400 uppercase">Avg Monthly Scrap</p>
                    <p className="text-lg font-bold font-mono text-white">{formatCurrency(monthlyScrap.summary.average_monthly)}</p>
                  </div>
                  <div className="bg-red-950/10 border border-red-500/20 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-red-400 uppercase">Highest Month</p>
                    <p className="text-sm font-bold text-red-400">{monthlyScrap.summary.highest_month}</p>
                    <p className="text-xs font-mono text-slate-400">{formatCurrency(monthlyScrap.summary.highest_month_value)}</p>
                  </div>
                  <div className="bg-green-950/10 border border-green-500/20 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-green-400 uppercase">Lowest Month</p>
                    <p className="text-sm font-bold text-green-400">{monthlyScrap.summary.lowest_month}</p>
                    <p className="text-xs font-mono text-slate-400">{formatCurrency(monthlyScrap.summary.lowest_month_value)}</p>
                  </div>
                  <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3 text-center">
                    <p className="text-[10px] text-slate-400 uppercase">Scrap Incidents</p>
                    <p className="text-lg font-bold font-mono text-orange-400">{monthlyScrap.summary.incident_count}</p>
                  </div>
                </div>
              )}

              {/* Monthly Trend Chart (quarterly + annual) */}
              {monthlyScrap.monthly_trend && monthlyScrap.monthly_trend.length > 0 && (
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
                  <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-bold">
                    {monthlyScrap.period === 'annual' ? 'Monthly Scrap Trend (Year)' : 'Monthly Breakdown (Quarter)'}
                  </h4>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyScrap.monthly_trend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#94a3b8" tickFormatter={v => `R${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={v => [formatCurrency(v), 'Value']} contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px' }} />
                        <Bar dataKey="value" fill="#ef4444" radius={[4, 4, 0, 0]} name="Scrap Value" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Year-on-Year Comparison Chart (annual only) */}
              {monthlyScrap.period === 'annual' && monthlyScrap.prev_year_trend && (
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
                  <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-bold">Year-on-Year Comparison ({monthlyScrap.year} vs {monthlyScrap.year - 1})</h4>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyScrap.monthly_trend.map((m, i) => ({
                        month: m.month,
                        current: m.value,
                        previous: monthlyScrap.prev_year_trend[i]?.value || 0,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                        <YAxis stroke="#94a3b8" tickFormatter={v => `R${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={v => [formatCurrency(v)]} contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px' }} />
                        <Legend />
                        <Bar dataKey="current" name={`${monthlyScrap.year}`} fill="#ef4444" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="previous" name={`${monthlyScrap.year - 1}`} fill="#64748b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Charts - Department + Component */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {monthlyScrap.by_department.length > 0 && (
                  <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
                    <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-bold">Scrap Value by Department</h4>
                    <div className="h-[220px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={monthlyScrap.by_department}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="department" stroke="#94a3b8" tick={{ fontSize: 9 }} angle={-30} textAnchor="end" height={60} />
                          <YAxis stroke="#94a3b8" tickFormatter={v => `R${(v/1000).toFixed(0)}k`} />
                          <Tooltip formatter={v => [formatCurrency(v), 'Value']} contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px' }} />
                          <Bar dataKey="scrap_value" fill="#ef4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {monthlyScrap.by_component.length > 0 && (
                  <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
                    <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-bold">Scrap Value by Component</h4>
                    <div className="h-[220px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={monthlyScrap.by_component.slice(0, 8)} dataKey="scrap_value" nameKey="part_number" cx="50%" cy="50%" outerRadius={80} label={({ part_number, percentage_of_total }) => `${part_number} (${percentage_of_total}%)`}>
                            {monthlyScrap.by_component.slice(0, 8).map((_, idx) => (
                              <Cell key={idx} fill={['#ef4444','#f59e0b','#22d3ee','#a855f7','#10b981','#6366f1','#ec4899','#84cc16'][idx % 8]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={v => [formatCurrency(v), 'Value']} contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>

              {/* Department Table */}
              {monthlyScrap.by_department.length > 0 && (
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
                  <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-bold">Scrap by Department / Process</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-slate-500 uppercase tracking-wider border-b border-slate-700">
                        <th className="text-left py-2 px-2">Department</th><th className="text-right py-2 px-2">Qty</th><th className="text-right py-2 px-2">Value</th><th className="text-right py-2 px-2">% of Total</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {monthlyScrap.by_department.map((dd, i) => (
                          <tr key={i} className="hover:bg-slate-800/30">
                            <td className="py-1.5 px-2 text-white font-bold">{dd.department}</td>
                            <td className="py-1.5 px-2 text-right text-white">{dd.scrap_quantity}</td>
                            <td className="py-1.5 px-2 text-right text-red-400 font-bold font-mono">{formatCurrency(dd.scrap_value)}</td>
                            <td className="py-1.5 px-2 text-right text-orange-400 font-bold">{dd.percentage_of_total}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Component Table */}
              {monthlyScrap.by_component.length > 0 && (
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
                  <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-bold">Scrap by Component</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-slate-500 uppercase tracking-wider border-b border-slate-700">
                        <th className="text-left py-2 px-2">Part Number</th><th className="text-left py-2 px-2">Description</th><th className="text-right py-2 px-2">Qty</th><th className="text-right py-2 px-2">Value</th><th className="text-right py-2 px-2">% of Total</th><th className="text-right py-2 px-2">Orders</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {monthlyScrap.by_component.map((c, i) => (
                          <tr key={i} className="hover:bg-slate-800/30">
                            <td className="py-1.5 px-2 font-mono text-cyan-400">{c.part_number}</td>
                            <td className="py-1.5 px-2 text-white">{c.description}</td>
                            <td className="py-1.5 px-2 text-right text-white font-bold">{c.scrap_quantity}</td>
                            <td className="py-1.5 px-2 text-right text-red-400 font-bold font-mono">{formatCurrency(c.scrap_value)}</td>
                            <td className="py-1.5 px-2 text-right text-orange-400 font-bold">{c.percentage_of_total}%</td>
                            <td className="py-1.5 px-2 text-right text-slate-400">{c.order_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Reason Table */}
              {monthlyScrap.by_reason?.length > 0 && (
                <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
                  <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-3 font-bold">Scrap by Reason / NCR</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-slate-500 uppercase tracking-wider border-b border-slate-700">
                        <th className="text-left py-2 px-2">Reason</th><th className="text-right py-2 px-2">Count</th><th className="text-right py-2 px-2">Qty Affected</th>
                      </tr></thead>
                      <tbody className="divide-y divide-slate-800/50">
                        {monthlyScrap.by_reason.map((r, i) => (
                          <tr key={i} className="hover:bg-slate-800/30">
                            <td className="py-1.5 px-2 text-white">{r.reason}</td>
                            <td className="py-1.5 px-2 text-right text-orange-400 font-bold">{r.count}</td>
                            <td className="py-1.5 px-2 text-right text-white">{r.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
